import { useEffect, useId, useRef, useState } from 'react';
import { useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, Modal, Tooltip } from '@ds/feedback';
import { Checkbox, FormField, Input, Select, Switch, Textarea } from '@ds/forms';
import { Tabs } from '@ds/navigation';
import { EtiquetaChip } from '@/comuns/EtiquetaChip';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { MODOS_DE_DISTRIBUICAO, SLAS_DA_FILA, normalizarGatilho, rotuloDaDistribuicao, rotuloDoSla } from '@/data/cadastros';
import type {
    CargaDeAtendimentoECrm, Departamento, EscopoDaMensagemRapida, Etiqueta, FilaDeAtendimento, MensagemRapida, ModoDeDistribuicao,
} from '@/data/cadastros';
import { SeletorDeCor } from './SeletorDeCor';

type Secao = 'departamentos' | 'filas' | 'etiquetas' | 'rapidas';

const ROTULO_DO_ESCOPO: Record<EscopoDaMensagemRapida, { rotulo: string; tom: 'success' | 'info' | 'neutral' }> = {
    publica: { rotulo: 'Pública', tom: 'success' },
    gestao: { rotulo: 'Restrita', tom: 'info' },
    privada: { rotulo: 'Privada', tom: 'neutral' },
};

/** Como salvar, avisar e recarregar — igual em todos os cadastros desta aba. */
function useGravacao() {
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const [salvando, setSalvando] = useState(false);

    return {
        salvando,
        async gravar(acao: () => Promise<unknown>, sucesso: { title: string; description?: string }, falha: string): Promise<boolean> {
            setSalvando(true);
            try {
                await acao();
                aviso(sucesso);
                await revalidator.revalidate();
                return true;
            } catch (e) {
                aviso({ title: falha, description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
                return false;
            } finally {
                setSalvando(false);
            }
        },
    };
}

/**
 * Atendimentos & CRM — departamentos, filas, etiquetas e mensagens rápidas.
 *
 * A ordem das seções é a ordem de dependência: a fila pertence a um
 * departamento, e a etiqueta aponta para os dois. O banco recusa a fila de um
 * departamento pendurada em outro; a tela só evita que a pessoa tente.
 */
export function AtendimentoECrm({ carga }: { carga: CargaDeAtendimentoECrm }) {
    const [secao, setSecao] = useState<Secao>('departamentos');
    const [departamento, setDepartamento] = useState<Departamento | 'novo' | null>(null);
    const [fila, setFila] = useState<FilaDeAtendimento | 'nova' | null>(null);
    const [etiqueta, setEtiqueta] = useState<Etiqueta | 'nova' | null>(null);
    const [mensagem, setMensagem] = useState<MensagemRapida | 'nova' | null>(null);

    const nomeDoDepartamento = (id: string | undefined) => carga.departamentos.find((d) => d.id === id)?.nome ?? '—';
    const filasSemGente = carga.filas.filter((f) => f.ativa && f.membros.length === 0);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ margin: 0, maxWidth: '78ch', fontSize: 13, lineHeight: '19px', color: 'var(--text-secondary)' }}>
                Aqui você configura departamentos, filas de atendimento, etiquetas e mensagens rápidas. O departamento define
                quem responde; a fila e a etiqueta se amarram a ele.
            </p>

            <Tabs
                ariaLabel="Cadastros de atendimento" activeId={secao} onChange={(id) => setSecao(id as Secao)}
                tabs={[
                    { id: 'departamentos', label: 'Departamentos', count: carga.departamentos.length },
                    { id: 'filas', label: 'Filas de atendimento', count: carga.filas.length },
                    { id: 'etiquetas', label: 'Etiquetas', count: carga.etiquetas.length },
                    { id: 'rapidas', label: 'Mensagens rápidas', count: carga.mensagensRapidas.length },
                ]}
            />

            {secao === 'departamentos' && (
                <>
                    <Alert tone="neutral" title="Departamento primeiro, fila depois">
                        A fila de atendimento é amarrada a um departamento. Criar fila sem departamento produz fila órfã, que
                        ninguém sabe quem atende.
                    </Alert>
                    <Card
                        title="Departamentos" padding={0}
                        actions={<Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setDepartamento('novo')}>Novo departamento</Button>}
                    >
                        <DataTable<Departamento>
                            rows={carga.departamentos} rowKey="id" stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                            emptyMessage="Nenhum departamento ainda."
                            columns={[
                                {
                                    key: 'nome', header: 'Departamento',
                                    render: (d) => (
                                        <span>
                                            <strong style={{ display: 'block', fontWeight: 600 }}>{d.nome}</strong>
                                            {d.descricao && <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)' }}>{d.descricao}</span>}
                                        </span>
                                    ),
                                },
                                { key: 'filas', header: 'Filas', align: 'right', width: 90 },
                                { key: 'usuarios', header: 'Usuários', align: 'right', width: 100 },
                                { key: 'ativo', header: 'Situação', width: 120, render: (d) => <Badge tone={d.ativo ? 'success' : 'neutral'}>{d.ativo ? 'Ativo' : 'Desativado'}</Badge> },
                                { key: 'acoes', header: '', width: 100, align: 'right', render: (d) => <Button size="sm" variant="secondary" onClick={() => setDepartamento(d)}>Editar</Button> },
                            ] satisfies DataTableColumn<Departamento>[]}
                        />
                    </Card>
                </>
            )}

            {secao === 'filas' && (
                <>
                    <Card
                        title="Filas de atendimento" padding={0}
                        actions={(
                            <Button
                                size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setFila('nova')}
                                disabled={!carga.departamentos.some((d) => d.ativo)}
                            >
                                Nova fila
                            </Button>
                        )}
                    >
                        <DataTable<FilaDeAtendimento>
                            rows={carga.filas} rowKey="id" stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                            emptyMessage="Nenhuma fila ainda."
                            columns={[
                                {
                                    key: 'nome', header: 'Fila',
                                    render: (f) => (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
                                            <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: f.cor, flex: '0 0 auto' }} />
                                            <strong style={{ fontWeight: 600 }}>{f.nome}</strong>
                                        </span>
                                    ),
                                },
                                { key: 'departamentoId', header: 'Departamento', render: (f) => <Badge tone="neutral">{nomeDoDepartamento(f.departamentoId)}</Badge> },
                                {
                                    key: 'membros', header: 'Usuários vinculados',
                                    render: (f) => (f.membros.length
                                        ? f.membros.map((m) => m.nome).join(', ')
                                        : <span style={{ color: 'var(--warning-strong)', fontWeight: 600 }}>nenhum</span>),
                                },
                                { key: 'distribuicao', header: 'Distribuição', width: 130, render: (f) => rotuloDaDistribuicao(f.distribuicao) },
                                { key: 'slaMin', header: 'Tempo de resposta', align: 'right', width: 150, render: (f) => rotuloDoSla(f.slaMin) },
                                { key: 'ativa', header: 'Situação', width: 120, render: (f) => <Badge tone={f.ativa ? 'success' : 'neutral'}>{f.ativa ? 'Ativa' : 'Desativada'}</Badge> },
                                { key: 'acoes', header: '', width: 100, align: 'right', render: (f) => <Button size="sm" variant="secondary" onClick={() => setFila(f)}>Editar</Button> },
                            ] satisfies DataTableColumn<FilaDeAtendimento>[]}
                        />
                    </Card>
                    {filasSemGente.length > 0 && (
                        <Alert tone="warning" title={filasSemGente.length === 1 ? 'Uma fila está sem usuário vinculado' : `${filasSemGente.length} filas estão sem usuário vinculado`}>
                            Conversa que cai em fila sem gente fica sem resposta e não aparece na lista de ninguém.
                        </Alert>
                    )}
                </>
            )}

            {secao === 'etiquetas' && (
                <Card
                    title="Etiquetas" padding={0}
                    actions={<Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setEtiqueta('nova')}>Nova etiqueta</Button>}
                >
                    <DataTable<Etiqueta>
                        rows={carga.etiquetas} rowKey="id" stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                        emptyMessage="Nenhuma etiqueta ainda."
                        columns={[
                            { key: 'nome', header: 'Etiqueta', render: (e) => <EtiquetaChip nome={e.nome} cor={e.cor} /> },
                            { key: 'departamentoId', header: 'Departamento', render: (e) => nomeDoDepartamento(e.departamentoId) },
                            { key: 'filaId', header: 'Fila', render: (e) => carga.filas.find((f) => f.id === e.filaId)?.nome ?? '—' },
                            {
                                key: 'gatilhos', header: 'Gatilhos de tageamento',
                                render: (e) => (e.gatilhos.length
                                    ? (
                                        <span style={{ display: 'inline-flex', gap: 5, flexWrap: 'wrap' }}>
                                            {e.gatilhos.slice(0, 3).map((g) => (
                                                <span key={g} style={{ fontFamily: 'var(--font-mono)', fontSize: 12, padding: '1px 6px', background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-body)' }}>{g}</span>
                                            ))}
                                            {e.gatilhos.length > 3 && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>+{e.gatilhos.length - 3}</span>}
                                        </span>
                                    )
                                    : <span style={{ color: 'var(--text-tertiary)' }}>manual</span>),
                            },
                            { key: 'ativa', header: 'Situação', width: 120, render: (e) => <Badge tone={e.ativa ? 'success' : 'neutral'}>{e.ativa ? 'Ativa' : 'Desativada'}</Badge> },
                            { key: 'acoes', header: '', width: 100, align: 'right', render: (e) => <Button size="sm" variant="secondary" onClick={() => setEtiqueta(e)}>Editar</Button> },
                        ] satisfies DataTableColumn<Etiqueta>[]}
                    />
                </Card>
            )}

            {secao === 'rapidas' && (
                <>
                    <Alert tone="neutral" title="No chat, digite / para chamar a mensagem">
                        O gatilho aparece ao digitar <code style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>/</code> no campo de resposta
                        da Comunicação, ou pelo botão de mensagem rápida ao lado do campo.
                    </Alert>
                    <Card
                        title="Mensagens rápidas" padding={0}
                        actions={<Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setMensagem('nova')}>Nova mensagem rápida</Button>}
                    >
                        <DataTable<MensagemRapida>
                            rows={carga.mensagensRapidas} rowKey="id" stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                            emptyMessage="Nenhuma mensagem rápida ainda."
                            columns={[
                                { key: 'gatilho', header: 'Gatilho', width: 140, render: (m) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600, color: 'var(--text-brand)' }}>/{m.gatilho}</span> },
                                { key: 'titulo', header: 'Título', render: (m) => <strong style={{ fontWeight: 600 }}>{m.titulo}</strong> },
                                {
                                    key: 'texto', header: 'Mensagem',
                                    render: (m) => <span style={{ display: 'block', maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text-secondary)' }}>{m.texto}</span>,
                                },
                                { key: 'escopo', header: 'Visibilidade', width: 120, render: (m) => <Badge tone={ROTULO_DO_ESCOPO[m.escopo].tom}>{ROTULO_DO_ESCOPO[m.escopo].rotulo}</Badge> },
                                { key: 'usos', header: 'Usos', align: 'right', width: 80 },
                                { key: 'acoes', header: '', width: 100, align: 'right', render: (m) => <Button size="sm" variant="secondary" onClick={() => setMensagem(m)}>Editar</Button> },
                            ] satisfies DataTableColumn<MensagemRapida>[]}
                        />
                    </Card>
                </>
            )}

            {departamento && <ModalDoDepartamento alvo={departamento} onFechar={() => setDepartamento(null)} />}
            {fila && <ModalDaFila alvo={fila} carga={carga} onFechar={() => setFila(null)} />}
            {etiqueta && <ModalDaEtiqueta alvo={etiqueta} carga={carga} onFechar={() => setEtiqueta(null)} />}
            {mensagem && <ModalDaMensagemRapida alvo={mensagem} podeGestao={carga.podeGestao} onFechar={() => setMensagem(null)} />}
        </div>
    );
}

/* ------------------------------------------------------------ departamento */

function ModalDoDepartamento({ alvo, onFechar }: { alvo: Departamento | 'novo'; onFechar: () => void }) {
    const novo = alvo === 'novo';
    const { salvando, gravar } = useGravacao();
    const idNome = useId();
    const idDescricao = useId();
    const [nome, setNome] = useState(novo ? '' : alvo.nome);
    const [descricao, setDescricao] = useState(novo ? '' : alvo.descricao);
    const [ativo, setAtivo] = useState(novo ? true : alvo.ativo);
    const sujo = novo ? nome.trim() !== '' || descricao.trim() !== '' : nome !== alvo.nome || descricao !== alvo.descricao || ativo !== alvo.ativo;

    const salvar = async () => {
        const ok = await gravar(
            () => dados().salvarDepartamento(novo ? null : alvo.id, { nome, descricao, ativo }),
            { title: novo ? 'Departamento criado' : 'Departamento atualizado', description: nome.trim() },
            'Não foi possível salvar o departamento',
        );
        if (ok) onFechar();
    };

    return (
        <Modal
            open width={480} title={novo ? 'Novo departamento' : 'Editar departamento'} onClose={onFechar}
            dirty={sujo} onSave={() => { void salvar(); }}
            footer={(
                <>
                    <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={() => { void salvar(); }} disabled={!nome.trim() || salvando}>{salvando ? 'Salvando…' : 'Salvar'}</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <FormField label="Nome" htmlFor={idNome} required>
                    <Input id={idNome} value={nome} onChange={setNome} placeholder="Recepção" />
                </FormField>
                <FormField label="Descrição" htmlFor={idDescricao} hint="O que este departamento resolve">
                    <Textarea id={idDescricao} rows={2} value={descricao} onChange={setDescricao} />
                </FormField>
                <Switch
                    checked={ativo} onChange={setAtivo} label="Departamento ativo"
                    description="Desativado, não recebe fila nova. As filas e etiquetas que já apontam para ele continuam."
                />
            </div>
        </Modal>
    );
}

/* -------------------------------------------------------------------- fila */

function ModalDaFila({ alvo, carga, onFechar }: { alvo: FilaDeAtendimento | 'nova'; carga: CargaDeAtendimentoECrm; onFechar: () => void }) {
    const nova = alvo === 'nova';
    const { salvando, gravar } = useGravacao();
    const idDepartamento = useId();
    const idNome = useId();
    const idSla = useId();
    const idDistribuicao = useId();
    const primeiroAtivo = carga.departamentos.find((d) => d.ativo)?.id ?? '';

    const [departamentoId, setDepartamentoId] = useState(nova ? primeiroAtivo : alvo.departamentoId);
    const [nome, setNome] = useState(nova ? '' : alvo.nome);
    const [slaMin, setSlaMin] = useState(nova ? 15 : alvo.slaMin);
    const [cor, setCor] = useState(nova ? '#2986FF' : alvo.cor);
    const [membrosIds, setMembrosIds] = useState<string[]>(nova ? [] : alvo.membros.map((m) => m.id));
    const [ativa, setAtiva] = useState(nova ? true : alvo.ativa);
    const [distribuicao, setDistribuicao] = useState<ModoDeDistribuicao>(nova ? 'manual' : alvo.distribuicao);

    const inicial = nova
        ? { departamentoId: primeiroAtivo, nome: '', slaMin: 15, cor: '#2986FF', membrosIds: [] as string[], ativa: true, distribuicao: 'manual' }
        : { departamentoId: alvo.departamentoId, nome: alvo.nome, slaMin: alvo.slaMin, cor: alvo.cor, membrosIds: alvo.membros.map((m) => m.id), ativa: alvo.ativa, distribuicao: alvo.distribuicao };
    const sujo = JSON.stringify(inicial) !== JSON.stringify({ departamentoId, nome, slaMin, cor, membrosIds, ativa, distribuicao });

    /* Departamento desativado não recebe fila nova — mas a fila que já é dele
       precisa continuar mostrando de quem é. */
    const opcoesDeDepartamento = carga.departamentos
        .filter((d) => d.ativo || d.id === departamentoId)
        .map((d) => ({ value: d.id, label: d.ativo ? d.nome : `${d.nome} (desativado)` }));
    /* Quem saiu da equipe continua aparecendo enquanto estiver vinculado: sumir
       da lista seria desvincular sem ninguém ter pedido. */
    const pessoas = [
        ...carga.membros,
        ...(nova ? [] : alvo.membros.filter((m) => !carga.membros.some((x) => x.id === m.id))),
    ];

    const salvar = async () => {
        const ok = await gravar(
            () => dados().salvarFila(nova ? null : alvo.id, { nome, departamentoId, cor, slaMin, ativa, distribuicao, membrosIds }),
            { title: nova ? 'Fila criada' : 'Fila atualizada', description: nome.trim() },
            'Não foi possível salvar a fila',
        );
        if (ok) onFechar();
    };

    return (
        <Modal
            open width={520} title={nova ? 'Nova fila de atendimento' : 'Editar fila'} onClose={onFechar}
            dirty={sujo} onSave={() => { void salvar(); }}
            footer={(
                <>
                    <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={() => { void salvar(); }} disabled={!nome.trim() || !departamentoId || salvando}>{salvando ? 'Salvando…' : 'Salvar'}</Button>
                </>
            )}
        >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 14 }}>
                <FormField label="Departamento" htmlFor={idDepartamento} required style={{ gridColumn: '1 / -1' }}
                    hint="A fila pertence a um departamento — é ele que define quem responde.">
                    <Select id={idDepartamento} value={departamentoId} onChange={setDepartamentoId} options={opcoesDeDepartamento} />
                </FormField>
                <FormField label="Nome da fila" htmlFor={idNome} required style={{ gridColumn: '1 / -1' }}>
                    <Input id={idNome} value={nome} onChange={setNome} placeholder="Recepção · Geral" />
                </FormField>
                <FormField label="Tempo de resposta esperado" htmlFor={idSla}>
                    <Select
                        id={idSla} value={String(slaMin)} onChange={(v) => setSlaMin(Number(v))}
                        options={SLAS_DA_FILA.map((s) => ({ value: String(s.minutos), label: s.rotulo }))}
                    />
                </FormField>
                <FormField label="Cor">
                    <SeletorDeCor valor={cor} onChange={setCor} rotulo="Cor da fila" />
                </FormField>
                <FormField
                    label="Distribuição das conversas" htmlFor={idDistribuicao} style={{ gridColumn: '1 / -1' }}
                    hint={`${MODOS_DE_DISTRIBUICAO.find((m) => m.id === distribuicao)?.descricao ?? ''} Vale para os usuários vinculados que estão online; a conversa distribuída espera em Pendentes até a pessoa aceitar.`}
                >
                    <Select
                        id={idDistribuicao} value={distribuicao} onChange={(v) => setDistribuicao(v as ModoDeDistribuicao)}
                        options={MODOS_DE_DISTRIBUICAO.map((m) => ({ value: m.id, label: m.rotulo }))}
                    />
                </FormField>
                <fieldset style={{ gridColumn: '1 / -1', border: 0, margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <legend style={{ marginBottom: 7, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' }}>Usuários vinculados</legend>
                    {pessoas.map((m) => (
                        <Checkbox
                            key={m.id} label={m.nome} checked={membrosIds.includes(m.id)}
                            onChange={(marcado) => setMembrosIds((l) => (marcado ? [...l, m.id] : l.filter((x) => x !== m.id)))}
                        />
                    ))}
                    <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>Só quem está aqui recebe as conversas desta fila.</span>
                </fieldset>
                <Switch checked={ativa} onChange={setAtiva} label="Fila ativa" style={{ gridColumn: '1 / -1' }} />
            </div>
        </Modal>
    );
}

/* ---------------------------------------------------------------- etiqueta */

function ModalDaEtiqueta({ alvo, carga, onFechar }: { alvo: Etiqueta | 'nova'; carga: CargaDeAtendimentoECrm; onFechar: () => void }) {
    const nova = alvo === 'nova';
    const { salvando, gravar } = useGravacao();
    const idNome = useId();
    const idDepartamento = useId();
    const idFila = useId();
    const idGatilhos = useId();

    const [nome, setNome] = useState(nova ? '' : alvo.nome);
    const [departamentoId, setDepartamentoId] = useState(nova ? '' : alvo.departamentoId ?? '');
    const [filaId, setFilaId] = useState(nova ? '' : alvo.filaId ?? '');
    const [gatilhos, setGatilhos] = useState(nova ? '' : alvo.gatilhos.join('\n'));
    const [cor, setCor] = useState(nova ? '#2986FF' : alvo.cor);
    const [ativa, setAtiva] = useState(nova ? true : alvo.ativa);

    const listaDeGatilhos = gatilhos.split('\n').map((g) => g.trim()).filter(Boolean);
    const inicial = nova
        ? { nome: '', departamentoId: '', filaId: '', gatilhos: [] as string[], cor: '#2986FF', ativa: true }
        : { nome: alvo.nome, departamentoId: alvo.departamentoId ?? '', filaId: alvo.filaId ?? '', gatilhos: alvo.gatilhos, cor: alvo.cor, ativa: alvo.ativa };
    const sujo = JSON.stringify(inicial) !== JSON.stringify({ nome, departamentoId, filaId, gatilhos: listaDeGatilhos, cor, ativa });

    const salvar = async () => {
        const ok = await gravar(
            () => dados().salvarEtiqueta(nova ? null : alvo.id, {
                nome, cor, ativa, gatilhos: listaDeGatilhos,
                ...(departamentoId ? { departamentoId } : {}),
                ...(departamentoId && filaId ? { filaId } : {}),
            }),
            { title: nova ? 'Etiqueta criada' : 'Etiqueta atualizada', description: nome.trim() },
            'Não foi possível salvar a etiqueta',
        );
        if (ok) onFechar();
    };

    return (
        <Modal
            open width={560} title={nova ? 'Nova etiqueta' : 'Editar etiqueta'} onClose={onFechar}
            description="Etiqueta organiza a conversa e pode ser aplicada automaticamente por palavra-chave."
            dirty={sujo} onSave={() => { void salvar(); }}
            footer={(
                <>
                    <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={() => { void salvar(); }} disabled={!nome.trim() || salvando}>{salvando ? 'Salvando…' : 'Salvar'}</Button>
                </>
            )}
        >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 14 }}>
                <FormField label="Nome da etiqueta" htmlFor={idNome} required style={{ gridColumn: '1 / -1' }}>
                    <Input id={idNome} value={nome} onChange={setNome} placeholder="Orçamento pendente" />
                </FormField>
                <FormField label="Departamento" htmlFor={idDepartamento}>
                    <Select
                        id={idDepartamento} value={departamentoId} placeholder="Sem departamento"
                        onChange={(v) => { setDepartamentoId(v); setFilaId(''); }}
                        options={carga.departamentos.filter((d) => d.ativo || d.id === departamentoId).map((d) => ({ value: d.id, label: d.nome }))}
                    />
                </FormField>
                <FormField label="Fila de atendimento" htmlFor={idFila} hint={departamentoId ? undefined : 'Escolha o departamento primeiro'}>
                    <Select
                        id={idFila} value={filaId} placeholder="Sem fila" disabled={!departamentoId}
                        onChange={setFilaId}
                        options={carga.filas.filter((f) => f.departamentoId === departamentoId).map((f) => ({ value: f.id, label: f.nome }))}
                    />
                </FormField>
                <FormField
                    label="Gatilhos para tageamento automático" htmlFor={idGatilhos} style={{ gridColumn: '1 / -1' }}
                    hint="Palavra ou frase que, aparecendo numa mensagem recebida, aplica a etiqueta sozinha. Uma por linha."
                >
                    <Textarea id={idGatilhos} rows={3} value={gatilhos} onChange={setGatilhos} placeholder={'quanto custa\nvalor\norçamento'} />
                </FormField>
                <FormField label="Cor da etiqueta" style={{ gridColumn: '1 / -1' }}>
                    <SeletorDeCor valor={cor} onChange={setCor} rotulo="Cor da etiqueta" />
                </FormField>
                <span style={{ gridColumn: '1 / -1' }}>
                    <EtiquetaChip nome={nome.trim() || 'Prévia da etiqueta'} cor={cor} />
                </span>
                <Switch checked={ativa} onChange={setAtiva} label="Etiqueta ativa" style={{ gridColumn: '1 / -1' }} />
            </div>
        </Modal>
    );
}

/* -------------------------------------------------------- mensagem rápida */

/* Os atalhos do WhatsApp são a marcação que a equipe já usa: o editor fala a
   mesma língua em vez de inventar outra. */
const FORMATOS = [
    { id: 'negrito', icone: 'bold', dica: 'Negrito · *texto*', marca: '*' },
    { id: 'italico', icone: 'italic', dica: 'Itálico · _texto_', marca: '_' },
    { id: 'tachado', icone: 'strikethrough', dica: 'Tachado · ~texto~', marca: '~' },
    { id: 'mono', icone: 'code', dica: 'Monoespaçado · ```texto```', marca: '```' },
];
const EMOJIS = ['🙂', '👍', '🙏', '✨', '📅', '💧', '☀️', '💳', '📍', '⚠️'];

function ModalDaMensagemRapida({ alvo, podeGestao, onFechar }: { alvo: MensagemRapida | 'nova'; podeGestao: boolean; onFechar: () => void }) {
    const nova = alvo === 'nova';
    const { salvando, gravar } = useGravacao();
    const idGatilho = useId();
    const idTitulo = useId();
    const idEscopo = useId();
    const idTexto = useId();
    const area = useRef<HTMLTextAreaElement>(null);
    const posicao = useRef<{ inicio: number; fim: number } | null>(null);

    const [gatilho, setGatilho] = useState(nova ? '' : alvo.gatilho);
    const [titulo, setTitulo] = useState(nova ? '' : alvo.titulo);
    const [escopo, setEscopo] = useState<EscopoDaMensagemRapida>(nova ? 'publica' : alvo.escopo);
    const [texto, setTexto] = useState(nova ? '' : alvo.texto);
    const [ativa, setAtiva] = useState(nova ? true : alvo.ativa);
    const [emojis, setEmojis] = useState(false);
    const [removendo, setRemovendo] = useState(false);

    const normalizado = normalizarGatilho(gatilho);
    const inicial = nova
        ? { gatilho: '', titulo: '', escopo: 'publica', texto: '', ativa: true }
        : { gatilho: alvo.gatilho, titulo: alvo.titulo, escopo: alvo.escopo, texto: alvo.texto, ativa: alvo.ativa };
    const sujo = JSON.stringify(inicial) !== JSON.stringify({ gatilho, titulo, escopo, texto, ativa });

    /* O cursor volta para onde estava depois de envolver a seleção — sem isto,
       formatar uma palavra jogaria o cursor para o fim do texto. */
    useEffect(() => {
        const el = area.current;
        const p = posicao.current;
        if (!el || !p) return;
        el.focus();
        el.setSelectionRange(p.inicio, p.fim);
        posicao.current = null;
    }, [texto]);

    const envolver = (marca: string) => {
        const el = area.current;
        const a = el?.selectionStart ?? texto.length;
        const b = el?.selectionEnd ?? texto.length;
        posicao.current = { inicio: a + marca.length, fim: b + marca.length };
        setTexto(texto.slice(0, a) + marca + texto.slice(a, b) + marca + texto.slice(b));
    };
    const inserir = (s: string) => {
        const el = area.current;
        const a = el?.selectionStart ?? texto.length;
        posicao.current = { inicio: a + s.length, fim: a + s.length };
        setTexto(texto.slice(0, a) + s + texto.slice(a));
    };

    const opcoesDeEscopo = [
        { value: 'publica', label: 'Pública — todos os usuários' },
        { value: 'privada', label: 'Privada — só eu' },
        ...(podeGestao || escopo === 'gestao' ? [{ value: 'gestao', label: 'Restrita — quem administra a clínica' }] : []),
    ];

    const salvar = async () => {
        const ok = await gravar(
            () => dados().salvarMensagemRapida(nova ? null : alvo.id, { gatilho, titulo, texto, escopo, ativa }),
            { title: nova ? 'Mensagem rápida criada' : 'Mensagem atualizada', description: `/${normalizado}` },
            'Não foi possível salvar a mensagem rápida',
        );
        if (ok) onFechar();
    };
    const remover = async () => {
        if (nova) return;
        const ok = await gravar(
            () => dados().removerMensagemRapida(alvo.id),
            { title: 'Mensagem rápida removida', description: `/${alvo.gatilho}` },
            'Não foi possível remover a mensagem rápida',
        );
        if (ok) onFechar();
    };

    const botaoDaBarra = {
        width: 28, height: 28, border: 0, borderRadius: 'var(--radius-xs)', background: 'transparent',
        color: 'var(--neutral-600)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    } as const;

    return (
        <Modal
            open width={620} title={nova ? 'Nova mensagem rápida' : 'Editar mensagem rápida'} onClose={onFechar}
            dirty={sujo} onSave={() => { void salvar(); }}
            footer={(
                <>
                    {!nova && !removendo && <Button variant="ghost" onClick={() => setRemovendo(true)}>Remover</Button>}
                    <span style={{ marginLeft: 'auto' }} />
                    <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={() => { void salvar(); }} disabled={!normalizado || !titulo.trim() || !texto.trim() || salvando}>
                        {salvando ? 'Salvando…' : 'Salvar'}
                    </Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {removendo && !nova && (
                    <Alert
                        tone="danger" title={`Remover /${alvo.gatilho}?`}
                        action={(
                            <span style={{ display: 'flex', gap: 8 }}>
                                <Button size="sm" variant="ghost" onClick={() => setRemovendo(false)}>Manter</Button>
                                <Button size="sm" variant="danger" onClick={() => { void remover(); }} disabled={salvando}>Remover</Button>
                            </span>
                        )}
                    >
                        Quem digitar o atalho deixa de encontrá-la. As conversas em que ela já foi usada não mudam.
                    </Alert>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 14 }}>
                    <FormField label="Gatilho" htmlFor={idGatilho} required
                        hint={normalizado && normalizado !== gatilho ? `Vira /${normalizado}` : 'Digite / no chat e este atalho aparece'}>
                        <Input
                            id={idGatilho} value={gatilho} onChange={setGatilho} placeholder="confirma"
                            iconLeft={<span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-tertiary)' }}>/</span>}
                        />
                    </FormField>
                    <FormField label="Título" htmlFor={idTitulo} required>
                        <Input id={idTitulo} value={titulo} onChange={setTitulo} placeholder="Confirmação de agendamento" />
                    </FormField>
                    <FormField
                        label="Visibilidade" htmlFor={idEscopo} required style={{ gridColumn: '1 / -1' }}
                        hint="Pública fica disponível para toda a equipe; privada, só para quem escreveu; restrita, só para quem administra a clínica."
                    >
                        <Select id={idEscopo} value={escopo} onChange={(v) => setEscopo(v as EscopoDaMensagemRapida)} options={opcoesDeEscopo} />
                    </FormField>
                </div>

                <div>
                    <label htmlFor={idTexto} style={{ display: 'block', marginBottom: 7, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' }}>
                        Mensagem <span aria-hidden="true" style={{ color: 'var(--danger)' }}>*</span>
                    </label>
                    <div style={{ border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-input)', overflow: 'visible', background: 'var(--surface)' }}>
                        <textarea
                            id={idTexto} ref={area} rows={5} value={texto} required
                            onChange={(e) => setTexto(e.target.value)}
                            placeholder="Use {paciente}, {data}, {hora} e {profissional} para personalizar."
                            style={{
                                width: '100%', border: 0, outline: 'none', resize: 'vertical', padding: '11px 13px', borderRadius: 'var(--radius-input)',
                                fontFamily: 'var(--font-ui)', fontSize: 14, lineHeight: '21px', color: 'var(--text-primary)', background: 'transparent',
                            }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '7px 9px', borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-secondary)', flexWrap: 'wrap', position: 'relative' }}>
                            <Tooltip label="Emoji">
                                <button type="button" aria-label="Emoji" aria-expanded={emojis} onClick={() => setEmojis((v) => !v)}
                                    style={{ ...botaoDaBarra, background: emojis ? 'var(--surface-selected)' : 'transparent' }}>
                                    <Icon name="smile" size={15} />
                                </button>
                            </Tooltip>
                            <span aria-hidden="true" style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 5px' }} />
                            {FORMATOS.map((f) => (
                                <Tooltip key={f.id} label={f.dica}>
                                    <button type="button" aria-label={f.dica} onClick={() => envolver(f.marca)} style={botaoDaBarra}>
                                        <Icon name={f.icone} size={14} />
                                    </button>
                                </Tooltip>
                            ))}
                            <Tooltip label="Remover formatação">
                                <button type="button" aria-label="Remover formatação" onClick={() => setTexto(texto.replace(/[*_~`]/g, ''))} style={botaoDaBarra}>
                                    <Icon name="remove-formatting" size={14} />
                                </button>
                            </Tooltip>
                            <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-ui)', fontSize: 12, color: 'var(--text-tertiary)' }}>
                                Atalhos do WhatsApp: *negrito* _itálico_ ~tachado~
                            </span>
                            {emojis && (
                                <div role="group" aria-label="Emojis" style={{ position: 'absolute', bottom: 40, left: 8, zIndex: 5, display: 'flex', gap: 4, flexWrap: 'wrap', width: 210, padding: 8, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)' }}>
                                    {EMOJIS.map((e) => (
                                        <button key={e} type="button" aria-label={`Inserir ${e}`} onClick={() => { inserir(e); setEmojis(false); }}
                                            style={{ width: 30, height: 30, border: 0, background: 'transparent', borderRadius: 'var(--radius-xs)', cursor: 'pointer', fontSize: 17 }}>
                                            {e}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <Switch checked={ativa} onChange={setAtiva} label="Mensagem ativa" description="Desativada, some do atalho / do chat e continua guardada." />
            </div>
        </Modal>
    );
}
